import { forwardRef, type ReactNode, type Ref } from 'react';
import { Button, type ButtonProps } from '@hungryroot/ramen';
import { STARTING_ROUTE } from '../../defs/routes';
import { useNavigationResumeUrl } from '../../hooks/useNavigationResumeUrl';
import {
  type AllFunnelRoutes,
  funnelRoutesSchema,
} from '../../navigation/funnel-routes';

type Props =
  | {
      prominence: ButtonProps['prominence'];
      style: ButtonProps['style'];
      size: ButtonProps['size'];
      disabled?: boolean;
      resumeQuizText?: string;
      startQuizText?: string;
      className?: string;
      startingRoute?: AllFunnelRoutes;
      children?: never;
    }
  | {
      startingRoute?: AllFunnelRoutes;
      resumeQuizText?: string;
      startQuizText?: string;
      children: (props: {
        text: string;
        route: AllFunnelRoutes | `/login/?next=${AllFunnelRoutes}`;
      }) => ReactNode;
    };

export const ResumeFunnelButton = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
    const {
      children,
      startQuizText = 'Take the Quiz',
      resumeQuizText = 'Finish Quiz',
      startingRoute = STARTING_ROUTE,
    } = props;

    const result = useNavigationResumeUrl({
      routesSchema: funnelRoutesSchema,
      defaultRoute: startingRoute,
    });

    if (startingRoute === undefined) {
      return null;
    }

    if (children !== undefined) {
      return children({
        text: result === undefined ? startQuizText : resumeQuizText,
        route: result === undefined ? startingRoute : result,
      });
    }

    if (result === undefined) {
      return (
        <Button {...props} ref={ref} buttonType="link" to={startingRoute}>
          {startQuizText}
        </Button>
      );
    }

    return (
      <Button {...props} ref={ref} buttonType="link" to={result}>
        {resumeQuizText}
      </Button>
    );
  }
);
